import { useAuth } from 'app/frontend/base/config/context/auth'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  usePageInfo,
  useTeamListEvents,
} from 'app/frontend/base/config/queries'
import usePersistedHashValue from 'app/frontend/base/hooks/usePersistedHashValue'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useEventParticipantStatusMutation from './useEventParticipantStatusMutation'
import EventListTable from './EventListTable'
import useBulkUserEventStatusMutation from './useBulkUserEventStatusMutation'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updateEventParticipantSignup } from 'app/frontend/base/api/event'
import { patchTeamEventReferenceCaches } from '../../TeamEvent/utils/teamEventReferenceCache'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

const isEventPageValue = (value: string): value is string => /^\d+$/.test(value)
const isEventListTab = (value: string): value is 'upcoming' | 'past' =>
  value === 'upcoming' || value === 'past'

const EventList = () => {
  const { team } = useTeam()
  const { accessLevel } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const {
    data: { user },
  } = usePageInfo()
  const { value: tab, setValue: setTab } = usePersistedHashValue<
    'upcoming' | 'past'
  >({
    defaultValue: 'upcoming',
    isValid: isEventListTab,
    hashKey: 'tab',
    persistInLocalStorage: false,
  })
  const { value: pageValue, setValue: setPageValue } =
    usePersistedHashValue<string>({
      defaultValue: '1',
      isValid: isEventPageValue,
      hashKey: 'page',
      persistInLocalStorage: false,
    })
  const page = Math.max(Number(pageValue), 1)
  const { data, isPending, isFetching } = useTeamListEvents(team.id, page, tab)
  const isLoadingEvents =
    isPending || (isFetching && (data?.page ?? page) !== page)
  const [updatingEventId, setUpdatingEventId] = useState<number>()
  const ownMembership = useMemo(
    () =>
      (team.members ?? []).find(
        (member) => member.characterReference.userId === user?.id,
      ),
    [team.members, user?.id],
  )
  const { selectableStatuses, updateStatus, isUpdatingStatus } =
    useEventParticipantStatusMutation(
      {
        characterId: ownMembership?.characterReference.id ?? 0,
        role: ownMembership?.role ?? 'ranged',
        specId: ownMembership?.specId ?? undefined,
      },
      {
        onSuccess: () => setUpdatingEventId(undefined),
        onError: () => setUpdatingEventId(undefined),
      },
    )
  const { bulkUpdateStatus, isBulkUpdating } = useBulkUserEventStatusMutation()
  const { mutate: saveWithComment } = useMutation({
    mutationFn: ({
      participantId,
      status,
      comment,
    }: {
      participantId: number
      status: wowaudit.EventParticipantReference['status']
      comment: string
    }) =>
      updateEventParticipantSignup(team.id, participantId, { status, comment }),
    onSuccess: (updatedEvent) => {
      patchTeamEventReferenceCaches(queryClient, team.id, updatedEvent)
      toast.success(t('shared.changes-saved'))
    },
    onError: (error) => displayErrorToast(error, t),
  })

  const onPageChange = useCallback(
    (nextPage: number) => setPageValue(nextPage.toString()),
    [setPageValue],
  )
  const onTabChange = useCallback(
    (nextTab: 'upcoming' | 'past') => {
      setTab(nextTab)
      setPageValue('1')
    },
    [setTab, setPageValue],
  )
  const onRowClick = useCallback(
    (event: wowaudit.EventReference) =>
      navigate(`${team.path}/events/${event.id}`, {
        state: { eventsReturn: window.location.search + window.location.hash },
      }),
    [navigate, team.path],
  )
  const membershipForEvent = useCallback(() => ownMembership, [ownMembership])
  const selectableStatusesForEvent = useCallback(
    () => selectableStatuses,
    [selectableStatuses],
  )
  const canAdminEditEvent = useCallback(
    () => accessLevel === 'admin',
    [accessLevel],
  )
  const onSelectStatus = useCallback(
    ({
      event,
      participant,
      nextStatus,
    }: {
      event: wowaudit.EventReference
      participant?: wowaudit.EventParticipantReference
      nextStatus: wowaudit.EventParticipantReference['status']
    }) => {
      setUpdatingEventId(event.id)
      updateStatus({
        eventId: event.id,
        eventParticipantId: participant?.id,
        nextStatus,
      })
    },
    [updateStatus],
  )
  const onBulkSelectStatus = useCallback(
    (
      eventIds: number[],
      nextStatus: wowaudit.EventParticipantReference['status'],
      comment?: string,
    ) => bulkUpdateStatus({ eventIds, status: nextStatus, comment }),
    [bulkUpdateStatus],
  )
  const onSaveWithComment = useCallback(
    ({
      participant,
      nextStatus,
      comment,
    }: {
      event: wowaudit.EventReference
      participant: wowaudit.EventParticipantReference
      nextStatus: wowaudit.EventParticipantReference['status']
      comment: string
    }) =>
      saveWithComment({
        participantId: participant.id,
        status: nextStatus,
        comment,
      }),
    [saveWithComment],
  )

  return (
    <EventListTable
      userId={user?.id}
      events={data?.events ?? []}
      page={data?.page ?? page}
      totalPages={data?.totalPages ?? 1}
      isLoadingEvents={isLoadingEvents}
      isUpdatingStatus={isUpdatingStatus}
      updatingEventId={updatingEventId}
      onPageChange={onPageChange}
      onRowClick={onRowClick}
      membershipForEvent={membershipForEvent}
      selectableStatusesForEvent={selectableStatusesForEvent}
      canAdminEditEvent={canAdminEditEvent}
      team={team}
      onSelectStatus={onSelectStatus}
      onSaveWithComment={onSaveWithComment}
      bulkSelectableStatuses={selectableStatuses}
      isBulkUpdating={isBulkUpdating}
      onBulkSelectStatus={onBulkSelectStatus}
      tab={tab}
      onTabChange={onTabChange}
    />
  )
}

export default EventList
